// AdminWaHealthPage — tablero GLOBAL de salud WhatsApp (super_admin, F5c).
// Una tarjeta por sesión WAHA: estado VIVO (webhook session.status), score y
// semáforo de la foto diaria (cron 07:15), gate anti-ban vigente y frenados de
// la semana. Expandir una sesión muestra los componentes del score y su línea
// de tiempo (wa_session_events, con la razón sacada de docker logs en caídas).
// Es la vista de Carlos: "¿qué número está en riesgo HOY y por qué?".

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import Layout from '../../../components/layout/Layout';
import SaludTendencia, { MOSTRAR_TENDENCIA_SALUD } from '../../whatsapp/components/SaludTendencia';
import {
  HeartPulse, RefreshCw, LifeBuoy, ChevronDown, ChevronUp, Shield, ShieldAlert,
  AlertTriangle, CalendarClock, Clock, Wifi, WifiOff, QrCode, History,
  Search, CheckCircle2, XCircle, Ticket, Minus,
} from 'lucide-react';
import {
  adminWaHealthApi, WaSessionRow, WaSessionEvent, WaSemaforo, GateExplainResult,
} from '../../../api/adminWaHealthApi';

// ── Formato ──────────────────────────────────────────────────────────────────
const fmtFecha = (s: string | null | undefined) =>
  s ? new Date(s.length === 10 ? `${s}T12:00:00` : s).toLocaleDateString('es-MX', { day: '2-digit', month: 'short' }) : '—';
const fmtFechaHora = (s: string | null | undefined) =>
  s ? new Date(s).toLocaleString('es-MX', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';
const diasDesde = (s: string | null | undefined) =>
  s ? Math.floor((Date.now() - new Date(s.length === 10 ? `${s}T12:00:00` : s).getTime()) / 86400000) : null;
const pct = (r: number | null | undefined) =>
  r === null || r === undefined ? null : `${Math.round(r * 100)}%`;

const SEMAFORO: Record<WaSemaforo, { label: string; chip: string; bar: string }> = {
  verde: { label: 'Saludable', chip: 'bg-green-500/15 text-green-600', bar: 'bg-green-500' },
  ambar: { label: 'Precaución', chip: 'bg-amber-500/15 text-amber-600', bar: 'bg-amber-500' },
  rojo: { label: 'En riesgo', chip: 'bg-red-500/15 text-red-600', bar: 'bg-red-500' },
};

// Estado vivo de la sesión → chip. WORKING es lo único "conectado"; el resto
// se pinta por gravedad (QR pendiente ≠ caída dura).
const LIVE: Record<string, { label: string; chip: string; Icon: React.ElementType }> = {
  WORKING: { label: 'Conectada', chip: 'bg-green-500/15 text-green-600', Icon: Wifi },
  STARTING: { label: 'Arrancando', chip: 'bg-amber-500/15 text-amber-600', Icon: Clock },
  OPENING: { label: 'Abriendo', chip: 'bg-amber-500/15 text-amber-600', Icon: Clock },
  SCAN_QR_CODE: { label: 'Esperando QR', chip: 'bg-amber-500/15 text-amber-600', Icon: QrCode },
  FAILED: { label: 'Caída', chip: 'bg-red-500/15 text-red-600', Icon: WifiOff },
  STOPPED: { label: 'Detenida', chip: 'bg-red-500/15 text-red-600', Icon: WifiOff },
};
const liveInfo = (status: string | null) =>
  LIVE[status || ''] || { label: status || 'Desconocido', chip: 'bg-gray-500/15 text-gray-500', Icon: WifiOff };

// Orden del tablero: lo que exige atención primero.
const rank = (r: WaSessionRow) => {
  if (r.live_status !== 'WORKING') return 0;
  if (r.snapshot.semaforo === 'rojo') return 1;
  if (r.snapshot.semaforo === 'ambar') return 2;
  return 3;
};

// ── Componentes del score (parts del motor v2) ──────────────────────────────
// Cada componente sale con su peso, si es medible, y un tooltip que explica qué
// mide y cómo puntúa. Orden: medibles primero por peso (importancia), los «—» al
// final — un componente sin datos suficientes NO cuenta ni castiga (prorrateo).
interface CompRow { label: string; pts: string; max: number; earned: number | null; detail: string; tip: string }
const compRows = (p: Record<string, any>): CompRow[] => {
  const out: CompRow[] = [];
  const push = (c: any, label: string, detail: string, tip: string) => {
    if (!c) return;
    const medido = c.pts !== null && c.pts !== undefined;
    // El backend manda `explica`: la frase con los números REALES de este número
    // (latest() la genera al leer). El texto genérico queda para fotos viejas.
    out.push({ label, max: Number(c.max) || 0, earned: medido ? Number(c.pts) : null,
      pts: medido ? `${c.pts}/${c.max}` : '—', detail: medido ? detail : `${detail} · sin datos: no cuenta ni castiga`,
      tip: c.explica || tip });
  };
  const r = p.respuesta, b = p.bidireccional, v = p.chats_vivos, m = p.carga_muda,
    f = p.frio, ma = p.madurez, h = p.higiene;
  push(r, 'Te responden', r ? `${r.respondieron ?? 0}/${r.medidos ?? 0} medidos${pct(r.rate) ? ` (${pct(r.rate)})` : ''}` : '',
    'Vale 30 (lo que MÁS pesa). De las campañas/felicitaciones/goteos con ≥48 h de vida, ¿cuántas recibieron respuesta? Meta 30%. Necesita ≥5 envíos medibles — si no, sale «—».');
  push(b, 'Ida y vuelta (90d)', b ? `${b.vivos ?? 0} de ${b.contrapartes ?? 0}${pct(b.rate) ? ` (${pct(b.rate)})` : ''}` : '',
    'Vale 20. De todas las personas con las que hubo chat en 90 días, ¿cuántas te han escrito ELLAS? Meta 70%. Necesita ≥10 contrapartes.');
  push(v, 'Personas que escriben', v ? (v.con_fuente ? `${v.vivos}` : 'sin captura (WEBJS)') : '',
    'Vale 10. Personas distintas que te escribieron en 90 días: 200+ pleno · 50+ parcial · <10 casi nada.');
  push(m, 'Carga muda (30d)', m ? `${m.proactivos_30d ?? 0} proactivos · ${m.sin_respuesta_est ?? '?'} sin respuesta` : '',
    'Vale 10. Mensajes proactivos SIN respuesta en 30 días — la señal que más castiga WhatsApp: <50 pleno · 400+ cero.');
  push(f, 'Envíos en frío (30d)', f ? `${f.frios ?? 0} de ${f.n ?? 0}${f.nuevos ? ` · ${f.nuevos} a números nuevos` : ''}` : '',
    'Vale 10. Proactivos a gente sin conversación previa. Si >5% van a números que JAMÁS te han escrito → 0 puntos (así cayó Silvestre). Necesita ≥5 envíos.');
  push(ma, 'Madurez', ma ? `${ma.dias ?? '?'} días${ma.desde ? ` (desde ${fmtFecha(ma.desde)})` : ''}` : '',
    'Vale 10. Días desde la vinculación del número: 60+ pleno · 30+ siete pts · 14+ cuatro · <14 cero (y capa el semáforo a ámbar).');
  push(h, 'Higiene de entrega (14d)', h ? `${h.fallos_14d ?? 0} fallos de ${h.envios_14d ?? 0}` : '',
    'Vale 10. Fallos de entrega en 14 días (números muertos, errores): <2% pleno · 10%+ cero. Necesita ≥10 envíos.');
  // Importancia: medibles primero, de mayor a menor peso; los «—» al final.
  return out.sort((a, b2) => (a.earned === null ? 1 : 0) - (b2.earned === null ? 1 : 0) || b2.max - a.max);
};

// ── Línea de tiempo de una sesión (fetch al expandir) ───────────────────────
const SessionTimeline: React.FC<{ session: string }> = ({ session }) => {
  const [events, setEvents] = useState<WaSessionEvent[] | null>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    adminWaHealthApi.events(session, 30)
      .then((r) => setEvents(r.events))
      .catch((e) => setError(e instanceof Error ? e.message : 'Error cargando eventos.'));
  }, [session]);

  if (error) return <p className="text-xs text-red-500">{error}</p>;
  if (!events) return <p className="text-xs text-content-3">Cargando línea de tiempo…</p>;
  if (!events.length) return <p className="text-xs text-content-3">Sin eventos registrados (la línea de tiempo acumula desde el 26-jul-2026).</p>;
  return (
    <ul className="space-y-1.5">
      {events.map((e, i) => {
        const li = liveInfo(e.status);
        return (
          <li key={i} className="flex items-start gap-2 text-xs">
            <span className="text-content-3 whitespace-nowrap tabular-nums">{fmtFechaHora(e.ts)}</span>
            <span className={`px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap ${li.chip}`}>{li.label}</span>
            <span className="text-content-2">
              {e.prev_status ? `desde ${liveInfo(e.prev_status).label.toLowerCase()}` : 'primera observación'}
              {e.reason ? ` · ${e.reason}` : ''}{e.detail && e.detail !== 'primera observación' ? ` · ${e.detail}` : ''}
              <span className="text-content-3"> ({e.source})</span>
            </span>
          </li>
        );
      })}
    </ul>
  );
};

// ── Tarjeta por sesión ───────────────────────────────────────────────────────
const SessionCard: React.FC<{ row: WaSessionRow; expanded: boolean; onToggle: () => void }> =
  ({ row, expanded, onToggle }) => {
    const snap = row.snapshot;
    const sem = snap.available ? SEMAFORO[snap.semaforo || 'rojo'] : null;
    const li = liveInfo(row.live_status);
    const dias = diasDesde(row.working_since);
    const r463 = snap.parts?.r463;
    const tlock = snap.parts?.timelock;       // sensor autoritativo (17-ago); fotos viejas no lo traen
    const gate = row.gate;
    const frenados = gate?.rechazos?.reduce((a, r) => a + r.n, 0) || 0;
    const gateActivo = gate && gate.restriccion && gate.restriccion !== 'ninguna';
    const gateTodo = gate?.restriccion === 'todo';

    return (
      <div className="bg-surface border border-line rounded-xl p-4 space-y-3">
        {/* Encabezado: negocio + sesión + estado vivo */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <p className="font-semibold text-content truncate">
              {row.tenant_name || row.session}
              {row.tenant_id ? <span className="font-normal text-content-3"> · #{row.tenant_id}</span> : null}
            </p>
            <p className="text-xs text-content-3 truncate">{row.session}</p>
          </div>
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full whitespace-nowrap ${li.chip}`}>
            <li.Icon className="h-3.5 w-3.5" /> {li.label}
          </span>
        </div>

        {/* Score + semáforo (de la foto diaria) */}
        {snap.available && sem ? (
          <div className="flex items-center gap-3">
            <span className="text-2xl font-bold text-content tabular-nums shrink-0">
              {snap.score}<span className="text-sm font-medium text-content-3">/100</span>
            </span>
            <div className="flex-1 h-2 rounded-full bg-surface-2 overflow-hidden">
              <div className={`h-full rounded-full ${sem.bar}`} style={{ width: `${snap.score || 0}%` }} />
            </div>
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${sem.chip}`}>{sem.label}</span>
          </div>
        ) : (
          <p className="text-sm text-content-3">Sin medición todavía (la foto se toma cada mañana a las 07:15).</p>
        )}
        {snap.veredicto && <p className="text-sm text-content-2">{snap.veredicto}</p>}

        {/* Restricción de WhatsApp (timelock) — la fuente manda: si WAHA dice que el número
            está restringido, se pinta aunque el ledger no tenga ningún 463 todavía. */}
        {tlock?.active && (
          <div className="rounded-lg px-3 py-2 bg-red-500/10">
            <p className="text-xs font-medium flex items-center gap-1.5 text-red-600">
              <ShieldAlert className="h-3.5 w-3.5" />
              Restricción de WhatsApp (timelock) activa
              <span className="font-normal">
                {tlock.hasta ? ` · hasta el ${fmtFecha(tlock.hasta)}` : ''}{tlock.tipo && tlock.tipo !== 'DEFAULT' ? ` · ${tlock.tipo}` : ''}
              </span>
            </p>
          </div>
        )}

        {/* Gate anti-ban vigente */}
        {gateActivo && (
          <div className={`rounded-lg px-3 py-2 ${gateTodo ? 'bg-red-500/10' : 'bg-amber-500/10'}`}>
            <p className={`text-xs font-medium flex items-center gap-1.5 ${gateTodo ? 'text-red-600' : 'text-amber-600'}`}>
              {gateTodo ? <ShieldAlert className="h-3.5 w-3.5" /> : <Shield className="h-3.5 w-3.5" />}
              {gateTodo ? 'Envíos masivos en pausa automática' : 'Solo clientes con conversación previa'}
              <span className="font-normal">
                · {gate!.label}{gate!.hasta ? ` · se libera el ${fmtFecha(gate!.hasta)}` : ''}
              </span>
            </p>
          </div>
        )}

        {/* Meta: vinculación, 463, frenados, medición */}
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-content-2">
          <span className="flex items-center gap-1">
            <CalendarClock className="h-3.5 w-3.5 text-content-3" />
            {dias !== null ? `Vinculada hace ${dias} d (${fmtFecha(row.working_since)})` : 'Sin fecha de vinculación'}
          </span>
          {r463?.dias !== null && r463?.dias !== undefined && (
            <span className="flex items-center gap-1 text-amber-600 cursor-help" title={r463.explica}>
              <AlertTriangle className="h-3.5 w-3.5" />
              463 hace {r463.dias} d{r463.c30 ? ` · ${r463.c30} en 30 d` : ''}
            </span>
          )}
          {frenados > 0 && (
            <span className="flex items-center gap-1">
              <Shield className="h-3.5 w-3.5 text-content-3" />
              {frenados} frenados esta semana
            </span>
          )}
          {snap.measured_at && (
            <span className="text-content-3">
              Medido {fmtFechaHora(snap.measured_at)}{snap.stale ? ' · ⚠️ atrasado' : ''}
            </span>
          )}
        </div>

        {/* Expandir: componentes + rechazos + timeline */}
        <button onClick={onToggle} className="flex items-center gap-1 text-xs font-medium text-accent">
          {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          {expanded ? 'Ocultar detalle' : 'Ver detalle'}
        </button>
        {expanded && (
          <div className="border-t border-line pt-3 space-y-4">
            {/* Tendencia del score. Oculta tras MOSTRAR_TENDENCIA_SALUD hasta tener
                ~3 semanas de fotos (hoy 4 días); el backend ya manda la serie. */}
            {MOSTRAR_TENDENCIA_SALUD && row.historia && (
              <div>
                <p className="text-xs font-semibold text-content mb-1.5">Tendencia del score</p>
                <SaludTendencia historia={row.historia} />
              </div>
            )}
            {snap.available && snap.parts && (() => {
              const rows = compRows(snap.parts);
              const earned = rows.reduce((a, c) => a + (c.earned ?? 0), 0);
              const avail = rows.reduce((a, c) => a + (c.earned !== null ? c.max : 0), 0);
              return (
                <div>
                  <p className="text-xs font-semibold text-content mb-1">Componentes del score <span className="font-normal text-content-3">(pasa el cursor sobre cada uno para ver qué mide)</span></p>
                  {avail > 0 && (
                    <p className="text-xs text-content-2 mb-2">
                      Solo cuenta lo medible: <span className="font-semibold text-content">{earned} de {avail} puntos</span> → {Math.round((earned / avail) * 100)}/100.
                      Los «—» aún no tienen datos suficientes y <span className="font-medium">no cuentan ni castigan</span> — por eso la suma directa no da el score.
                    </p>
                  )}
                  <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1">
                    {rows.map((c, i) => (
                      <div key={i} className="flex items-center justify-between text-xs cursor-help" title={c.tip}>
                        <span className={`underline decoration-dotted decoration-1 underline-offset-2 ${c.earned === null ? 'text-content-3' : 'text-content-2'}`}>{c.label}</span>
                        <span className={`whitespace-nowrap ${c.earned === null ? 'text-content-3' : 'text-content'}`}>
                          <span className="font-semibold tabular-nums">{c.pts}</span>
                          <span className="text-content-3"> · {c.detail}</span>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
            {(gate?.rechazos?.length ?? 0) > 0 && (
              <div>
                <p className="text-xs font-semibold text-content mb-1.5">Frenados por el gate (7 días)</p>
                <ul className="space-y-0.5">
                  {gate!.rechazos!.map((r, i) => (
                    <li key={i} className="text-xs text-content-2">
                      {r.n} envíos · {r.label} · {r.numeros} números · último {fmtFechaHora(r.ultima)}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div>
              <p className="text-xs font-semibold text-content mb-1.5 flex items-center gap-1">
                <History className="h-3.5 w-3.5" /> Línea de tiempo de la sesión
              </p>
              <SessionTimeline session={row.session} />
            </div>
          </div>
        )}
      </div>
    );
  };

// ── Página ───────────────────────────────────────────────────────────────────
// ── Explicador del gate (30-jul-2026) ────────────────────────────────────────
// «¿Por qué este contacto sí/no recibiría?» — consulta /wa-health/explain y pinta
// el recorrido regla por regla, marcando las que la conversación previa anula.
// Solo vive en esta página (super_admin): el trace expone internos (repaired,
// kicks) que no se le muestran al dueño del negocio.
const PASO_ICON: Record<string, { Icon: typeof CheckCircle2; cls: string }> = {
  ok: { Icon: CheckCircle2, cls: 'text-green-500' },
  bloquea: { Icon: XCircle, cls: 'text-red-500' },
  PASA: { Icon: Ticket, cls: 'text-amber-500' },
  no_aplica: { Icon: Minus, cls: 'text-content-3' },
  sin_dato: { Icon: Minus, cls: 'text-content-3' },
};

const GateExplica: React.FC<{ rows: WaSessionRow[] }> = ({ rows }) => {
  const tenants = useMemo(() =>
    rows.filter((r) => r.tenant_id)
      .map((r) => ({ id: r.tenant_id as number, name: r.tenant_name || `t${r.tenant_id}` }))
      .sort((a, b) => a.name.localeCompare(b.name)),
    [rows]);
  const [tenant, setTenant] = useState<number>(0);
  const [phone, setPhone] = useState('');
  const [source, setSource] = useState<'campaign' | 'birthday' | 'nurture'>('campaign');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [r, setR] = useState<GateExplainResult | null>(null);

  const consultar = async () => {
    const t = tenant || tenants[0]?.id;
    if (!t || !phone.trim()) { setErr('Elige el negocio y escribe un teléfono.'); return; }
    setBusy(true); setErr(''); setR(null);
    try { setR(await adminWaHealthApi.explain(t, phone.trim(), source)); }
    catch (e) { setErr(e instanceof Error ? e.message : 'No se pudo consultar.'); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-surface border border-line rounded-xl p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Search className="h-4 w-4 text-accent" />
        <h2 className="text-sm font-semibold text-content">¿Por qué sí / no le llega?</h2>
        <span className="text-[11px] text-content-3">recorre el gate anti-ban regla por regla, sin enviar nada</span>
      </div>

      <div className="flex flex-wrap gap-2">
        <select
          value={tenant || tenants[0]?.id || 0}
          onChange={(e) => setTenant(Number(e.target.value))}
          className="px-2 py-2 text-sm rounded-md border border-line bg-surface text-content"
        >
          {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <input
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && consultar()}
          placeholder="Teléfono (10 dígitos)"
          className="px-3 py-2 text-sm rounded-md border border-line bg-surface text-content w-44"
        />
        <select
          value={source}
          onChange={(e) => setSource(e.target.value as typeof source)}
          className="px-2 py-2 text-sm rounded-md border border-line bg-surface text-content"
        >
          <option value="campaign">Campaña</option>
          <option value="birthday">Cumpleaños</option>
          <option value="nurture">Goteo</option>
        </select>
        <button
          onClick={consultar}
          disabled={busy}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md bg-accent text-white hover:opacity-90 disabled:opacity-50"
        >
          {busy ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Consultar
        </button>
      </div>

      {err && <p className="text-sm text-red-600 dark:text-red-400">{err}</p>}

      {r && (
        <div className="space-y-3">
          <div className={`rounded-lg px-3 py-2 text-sm font-medium ${r.veredicto.ok
            ? 'bg-green-500/10 text-green-700 dark:text-green-300'
            : 'bg-red-500/10 text-red-700 dark:text-red-300'}`}
          >
            {r.veredicto.ok
              ? <>✅ El mensaje SALE — decidió: <b>{r.decidio}</b></>
              : <>🚫 El mensaje NO sale — motivo: <b>{r.veredicto.motivo}</b>
                {r.veredicto.permanente ? ' (permanente)' : ''}
                {r.veredicto.overridable ? ' · el operador puede forzarlo' : ' · no forzable'}</>}
            {!r.veredicto.ok && r.veredicto.detalle && (
              <p className="font-normal mt-1 opacity-80">{r.veredicto.detalle}</p>
            )}
          </div>

          {r.avisos.map((a, i) => (
            <div key={i} className="rounded-lg px-3 py-2 text-xs bg-amber-500/10 text-amber-700 dark:text-amber-300">
              {a}
            </div>
          ))}

          <div className="space-y-1.5">
            {r.pasos.map((p, i) => {
              const { Icon, cls } = PASO_ICON[p.estado] || PASO_ICON.no_aplica;
              const anulada = r.saltadas.includes(p.regla);
              return (
                <div key={i} className={`flex items-start gap-2 text-xs ${anulada ? 'opacity-90' : ''}`}>
                  <Icon className={`h-3.5 w-3.5 mt-0.5 flex-shrink-0 ${cls}`} />
                  <div className="min-w-0">
                    <p className="text-content">
                      <span className="font-mono font-medium">{p.regla}</span>
                      <span className="text-content-2"> · {p.dato}</span>
                      {anulada && (
                        <span className="ml-2 text-amber-600 dark:text-amber-400 font-medium">
                          ← anulada por la conversación previa
                        </span>
                      )}
                    </p>
                    <p className="text-content-3">{p.texto.replace(/\*\*/g, '')}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const AdminWaHealthPage: React.FC = () => {
  const { currentUser } = useAuth();
  const [rows, setRows] = useState<WaSessionRow[]>([]);
  const [expanded, setExpanded] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await adminWaHealthApi.overview();
      setRows(r.sessions);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error cargando el tablero.');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const sorted = useMemo(() =>
    [...rows].sort((a, b) => rank(a) - rank(b) || (a.tenant_name || a.session).localeCompare(b.tenant_name || b.session)),
    [rows]);

  const resumen = useMemo(() => {
    const caidas = rows.filter((r) => r.live_status !== 'WORKING').length;
    const s = (sem: WaSemaforo) => rows.filter((r) => r.live_status === 'WORKING' && r.snapshot.semaforo === sem).length;
    const frenados = rows.reduce((a, r) => a + (r.gate?.rechazos?.reduce((x, y) => x + y.n, 0) || 0), 0);
    return { caidas, verdes: s('verde'), ambar: s('ambar'), rojas: s('rojo'), frenados };
  }, [rows]);

  if (currentUser?.role !== 'admin') {
    return (
      <Layout>
        <div className="bg-red-50 dark:bg-red-500/10 p-4 rounded-md shadow border border-red-200 animate-fade-in-up">
          <div className="flex">
            <LifeBuoy className="h-5 w-5 text-red-400 flex-shrink-0" aria-hidden="true" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-300">Acceso restringido</h3>
              <p className="mt-2 text-sm text-red-700 dark:text-red-300">Solo los administradores pueden ver la salud de WhatsApp.</p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-5 animate-fade-in-up">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-xl font-bold text-content flex items-center gap-2">
              <HeartPulse className="h-6 w-6 text-accent" /> Salud WhatsApp
            </h1>
            <p className="text-sm text-content-2 mt-0.5">
              Todas las sesiones: estado en tiempo real, score anti-ban y protecciones activas.
            </p>
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md border border-line bg-surface text-content hover:bg-surface-2 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Actualizar
          </button>
        </div>

        {error && (
          <div className="bg-red-50 dark:bg-red-500/10 border border-red-200 rounded-md p-3 text-sm text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        {/* Resumen */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {[
            { label: 'Saludables', value: resumen.verdes, dot: 'bg-green-500' },
            { label: 'Precaución', value: resumen.ambar, dot: 'bg-amber-500' },
            { label: 'En riesgo', value: resumen.rojas, dot: 'bg-red-500' },
            { label: 'Desconectadas', value: resumen.caidas, dot: 'bg-gray-400' },
            { label: 'Frenados 7 d', value: resumen.frenados, dot: 'bg-accent' },
          ].map((c) => (
            <div key={c.label} className="bg-surface border border-line rounded-lg px-4 py-3">
              <p className="text-lg font-bold text-content leading-none tabular-nums flex items-center gap-2">
                <span className={`h-2 w-2 rounded-full ${c.dot}`} /> {loading ? '…' : c.value}
              </p>
              <p className="text-xs text-content-2 mt-1">{c.label}</p>
            </div>
          ))}
        </div>

        {/* Explicador del gate: ¿por qué este contacto sí/no recibiría? */}
        {!loading && rows.length > 0 && <GateExplica rows={rows} />}

        {/* Tarjetas por sesión (lo grave primero) */}
        {loading && !rows.length ? (
          <p className="text-sm text-content-3">Cargando sesiones…</p>
        ) : (
          <div className="grid lg:grid-cols-2 gap-4">
            {sorted.map((r) => (
              <SessionCard
                key={r.session}
                row={r}
                expanded={expanded === r.session}
                onToggle={() => setExpanded(expanded === r.session ? '' : r.session)}
              />
            ))}
          </div>
        )}

        <p className="text-[11px] text-content-3">
          La foto del score se toma cada mañana a las 07:15; el estado de conexión y la línea de
          tiempo llegan al instante por webhook. El gate anti-ban solo frena campañas, cumpleaños
          y goteos — lo transaccional (citas, recibos) nunca se bloquea.
        </p>
      </div>
    </Layout>
  );
};

export default AdminWaHealthPage;
